import { toast } from 'react-toastify';
import statusModal from '@pages/Forms/components/modals/statusModal';
import { useModal, useFormStore, useApp } from '@stores';
import { LoadingMessages, RequestStatus, ResponseStatuses, StorageScopes } from '@src/utils/enums';
import { queryClient } from '@src/api/queryClient';
import { DASHBOARD_QUERY_KEYS } from '@src/api/services/dashboard';
import { PROJECT_QUERY_KEYS } from '@src/api/services/project';
import SelfVerifyResultModal from '@src/app-pages/Forms/components/modals/self-verify-result/SelfVerifyResultModal';
import { resolveNprConfigForRequest } from '@src/app-pages/Forms/registry/configs/npr/nprConfig';
import { router } from '@src/Router';

const handleSuccess = (signed, body, store, dispatch) => {
  const { setIsLoading, setIsSavingProgress } = useApp.getState();
  const { openModal } = useModal.getState();
  const { setApiAnswers } = useFormStore.getState();

  setIsSavingProgress(false);
  setIsLoading(false);
  if (signed) {
    const nprResult = body.data?.request?.noPermitRequireds?.[0].nprResult;
    toast.success('Request has been successfully submitted!');
    openModal(SelfVerifyResultModal, {
      result: nprResult,
    });
    router.navigate({ to: '/dashboard' });
  } else {
    store.doUpdateNprRequest(body.data.request.noPermitRequireds[0]);
    setApiAnswers(body.data);
    dispatch({ type: 'UPDATE_REQUEST_API_DATA', payload: body.data });
    dispatch({ type: 'UPDATE_REQUEST_FORM_DATA', payload: body.data });
    dispatch({ type: 'UPDATE_REQUEST_OBJECT', payload: { requestStatus: RequestStatus.Draft } });
    toast.success('Request has been successfully saved!');
  }
};

const resetHiddenNprAnswers = (
  noPermitRequireds,
  questionVisibility,
  questionIdByName,
  excludeFields = [
    'createdBy',
    'createdDate',
    'modifiedBy',
    'modifiedDate',
    'requestID',
    'version',
    'nprResult',
    'noPermitRequiredID',
    'hasSupportingDocuments',
  ]
) => {
  const results = noPermitRequireds.map((answers) => {
    return Object.fromEntries(
      Object.entries(answers).filter(([key, value]) => {
        // Skip excluded metadata fields
        if (excludeFields.includes(key)) {
          return [key, value];
        }

        const qid = questionIdByName?.[key];
        if (!qid) {
          return [key, value];
        }
        // Check if question is visible
        const isHidden = !questionVisibility[qid];
        if (isHidden) {
          return undefined;
        }
        return [key, value];
      })
    );
  });
  return results[0];
};

const nprFormDataBundle = {
  name: 'nprFormData',

  getReducer: () => {
    const initialState = {
      noPermitRequireds: [],
      errors: {},
    };

    return (state = initialState, { type, payload }) => {
      switch (type) {
        case 'RESET_NPR':
          return initialState;
        case 'UPDATE_NPR':
          return { ...state, noPermitRequireds: [{ ...state.noPermitRequireds[0], ...payload }] };
        case 'UPDATE_NPR_ERRORS':
          return { ...state, errors: payload };
        default:
          return state;
      }
    };
  },

  selectNprData: (state) => state.nprFormData.noPermitRequireds?.[0],
  selectNprID: (state) => state.nprFormData.noPermitRequireds?.[0]?.noPermitRequiredID,
  selectNprErrors: (state) => state.nprFormData.errors,

  doUploadAndUpdateNpr:
    (data, status) =>
    ({ store }) => {
      const { setIsLoading, setIsSavingProgress } = useApp.getState();
      const { openSecondaryModal } = useModal.getState();

      const signed = Boolean(data?.request?.isSubmit);
      setIsSavingProgress(true);
      signed && setIsLoading(true, LoadingMessages.SubmitRequest);

      if (store.selectValidFiles()?.length > 0) {
        // Format to FormData
        const formdata = new FormData();
        formdata.append('overwrite', false);
        formdata.append('isAnonymous', false);
        store.selectValidFiles().forEach((item) => {
          const fileMetaData = {
            ProjectID: store.selectProjectID(),
            RequestID: store.selectRequestID(),
            Version: 0,
            Type: status,
            Tags: (item?.tags).map((tag) =>
              tag.section ? { ...tag, section: tag.section.replace(/[()]/g, '').replace(/\s+/g, ' ').trim() } : tag
            ),
          };
          formdata.append('files', item?.file);
          formdata.append('fileMetaData', JSON.stringify(fileMetaData));
        });

        Promise.all([store.doUploadFiles(formdata)])
          .then((results) => {
            if (results?.[0]?.status === ResponseStatuses.Success) {
              // if all files upload successfully
              store.doUpdateNpr(data);
              store.doResetUsersFileData();
              store.doFetchUserFilesDetails({ componentID: store.selectNprID(), scope: StorageScopes.ProjectFolder });
            } else {
              // if any of the files failed to upload
              setIsSavingProgress(false);
              store.doResetUsersFileData();
              openSecondaryModal(statusModal, {
                status: 'ERROR! Failed Uploads',
                msg: (
                  <>
                    <p>{results?.[0]?.msg}</p>
                    <p>Retry saving/submitting your Pre-application Request in a few minutes.</p>{' '}
                  </>
                ),
                saveText: 'Return to request',
              });
            }
          })
          .catch((e) => {
            console.error(e);
          });
      } else {
        // If no files were selected, just update NPR
        store.doUpdateNpr(data);
      }
    },
  doUpdateNpr:
    (data) =>
    ({ apiPut, dispatch, store }) => {
      const { setIsLoading, setIsSavingProgress } = useApp.getState();
      const { openSecondaryModal } = useModal.getState();
      const { questionVisibility: questionVisibilityObj } = useFormStore.getState();

      const signed = Boolean(data.request.isSubmit);
      setIsSavingProgress(true);
      setIsLoading(true, signed ? LoadingMessages.SubmitRequest : LoadingMessages.SaveRequest);

      const questionVisibility = questionVisibilityObj;

      const uri = '/api/NoPermitRequired/UpdateNPR';

      // Resolve correct NPR config for versioned question ID mapping
      const requestType = data?.request?.requestType;
      const formVersion = data?.request?.formVersion;
      const cfg = resolveNprConfigForRequest({ requestType, formVersion });

      if (data?.request?.noPermitRequireds?.[0] && questionVisibility) {
        data.request.noPermitRequireds[0] = resetHiddenNprAnswers(
          data.request.noPermitRequireds,
          questionVisibility,
          cfg.nprQuestionIdByName
        );
      }

      return apiPut(uri, data, (err, body) => {
        if (
          !err &&
          [ResponseStatuses.Success, ResponseStatuses.SuccessWithWarning, ResponseStatuses.Warning].includes(
            body?.status
          )
        ) {
          handleSuccess(signed, body, store, dispatch);
          queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_KEYS.GET_DASHBOARD] });
          queryClient.invalidateQueries({
            queryKey: [
              PROJECT_QUERY_KEYS.GET_SHARED_CONTACTS,
              store.selectProjectID(),
              store.selectRequestID(),
              store.selectVersion(),
            ],
          });
          queryClient.invalidateQueries({ queryKey: [PROJECT_QUERY_KEYS.GET_PROJECT_LOCATION] });
        } else {
          setIsSavingProgress(false);
          setIsLoading(false);
          openSecondaryModal(statusModal, {
            msg: body?.msg ?? err?.msg ?? 'An error occurred while saving or submitting the request, please try again',
            status: body?.status ?? err?.status ?? 'Request Save/Submission Error',
          });
          dispatch({ type: 'UPDATE_NPR_ERRORS', payload: body?.data });
        }
      });
    },
  doUpdateNprRequest:
    (data) =>
    ({ dispatch, store }) => {
      dispatch({ type: 'UPDATE_NPR', payload: data });
      dispatch({ type: 'UPDATE_REQUEST_OBJECT', payload: { noPermitRequireds: [store.selectNprData()] } });
    },
  doResetNprRequest:
    () =>
    ({ dispatch }) => {
      dispatch({ type: 'RESET_NPR' });
    },
};

export default nprFormDataBundle;
